// The Briefing's "N email groups" entry, opened (busy-main-bff#24, AI Brain spec §6/§10).
//
// The entry used to name the SENDER clusters and open nothing at all: the count came from
// `v1/email/groups` and the centre cleared its list. The number is now the user's own Email Groups
// (busy-my-web#274) — the named collections a person makes on the Work-board — and clicking it
// lists them, exactly as every other entry lists what it counts.
//
// What this module owns is the join. Two answers describe the same groups:
//
//   • the Briefing's own listing (`GET email/briefing/threads?entry=groups`) — id, title, thread
//     count, when it last moved, and the first few SUBJECTS in it, which is the part the centre
//     needs and nothing else has;
//   • the group records themselves (`GET email/groups/user`, already read by the panel) — which
//     carry the `timelineIds`, the `kind` and the archived flag the #274 ACTIONS act on.
//
// Merging them here means the centre offers the same verbs the strip does, against the same group
// objects, rather than a second set of actions written against a thinner row. Sender clusters are
// untouched by all of this: they keep their own listing, their own endpoint and their own module
// (lib/emailSession.js) — they simply stopped being what "email groups" counts.
//
// Pure — no store, no router, nothing mounted.

import { userGroupRows } from '@/lib/emailGroups'

/**
 * The Briefing's groups as full group records.
 *
 * The Briefing's row is authoritative about what it answered — it is the list the user is looking
 * at, in the server's order — and the known record fills in what a listing row does not carry. A
 * group the panel has never read is still listed: its actions are the ones a group with no known
 * membership can do, which is what `groupActions` already decides.
 */
export function briefingGroups(listed, known = []) {
  const byId = new Map((known || []).filter(g => g && g.id).map(g => [g.id, g]))
  return (listed || [])
    .filter(g => g && g.id)
    .map((g) => {
      const record = byId.get(g.id) || {}
      return {
        ...record,
        id: g.id,
        title: String(g.title || record.title || '').trim(),
        // The Briefing counted the group server-side; the ids are only what the panel happens to hold.
        threadCount: Number(g.threadCount ?? record.threadCount ?? 0) || 0,
        updatedUtc: g.updatedUtc ?? record.updatedUtc ?? null,
        timelineIds: [...(record.timelineIds || [])],
        kind: record.kind || g.kind || 'user',
        // The entry lists LIVE groups only, so anything in it is live — an archived group has
        // already dropped out of the answer, and out of the count that opened it.
        isArchived: false,
        subjects: (g.subjects || []).map(s => String(s || '').trim()).filter(Boolean),
      }
    })
}

/**
 * "Renewal quote · Policy documents" — the sample of what is IN a group, under its name.
 *
 * A group is a collection someone named, and the name alone does not say what they put in it. The
 * subjects are the server's first few, joined with the same middot the workspace uses between facts.
 */
export function groupSubjectLine(subjects) {
  return (subjects || []).map(s => String(s || '').trim()).filter(Boolean).join(' · ')
}

/** The listing, ready to render: #274's own row, plus the subjects only the Briefing answers with. */
export function briefingGroupRows(listed, known = [], now = new Date()) {
  const groups = briefingGroups(listed, known)
  return userGroupRows(groups, now).map((row, i) => ({
    ...row,
    subjects: groups[i].subjects,
    subjectLine: groupSubjectLine(groups[i].subjects),
  }))
}

/** One group out of the listing — what an archive does to the list it was archived from. */
export function withoutGroup(listed, groupId) {
  if (!groupId) return [...(listed || [])]
  return (listed || []).filter(g => g && g.id !== groupId)
}

/** The line under an empty listing. The group list is not a mailbox: it is empty until someone files. */
export function emptyGroupsLabel() {
  return 'No email groups yet — pick some threads and file them into one.'
}
